import React, { FC } from "react";
import { useAxios } from "../../../utils/hooks";
import useSafeState from "../../../utils/safe-state";
import { Trans } from "@lingui/macro";
import { Breadcrumb, Button, Divider, message, Popconfirm, Tooltip, Upload, UploadProps } from "antd";
import {
    DeleteTwoTone,
    PlusOutlined,
    UploadOutlined
} from "@ant-design/icons/lib";
import DtoTable from "../../../components/dto-table/dto-table";
import ButtonWithIcon from "../../../components/buttons/button-with-icon";
import LocationModel from "../../../models/locations/location-model";
import Drawer from "../../../components/drawer/drawer";
import ItemDrawer from "#/forms/item-form";
import LocationDrawer from "../../../forms/location-form";
import AddLocationForm from "../../../forms/add-location-form";
import UploadLocationExcelForm from "../../../forms/upload-location-excel-form";
import { DownloadOutlined, EditOutlined, EyeOutlined, CopyOutlined } from "@ant-design/icons";
import { openNotification } from "../../../utils/openNotification";
import FileDownload from "js-file-download";

const LocationsScreen: FC = () => {

    const [trigger, setTrigger] = useSafeState(false);
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const locationText = <Trans>Location</Trans>
    const addLocationText = <Trans>Location</Trans>
    const uploadLocationText = <Trans>Upload Locations from Excel</Trans>

    const [locFileList, setLocFileList] = useSafeState<Array<any>>([]);
    const [locLoading, setLocLoading] = useSafeState(false);


    const locationDrawer = Drawer(
        {
            width: 1024,
            title: locationText,
            children:
                <LocationDrawer />,
            customClose: () => setTrigger(function(prev) { return !prev; })
        }
    );

    const addLocationDrawer = Drawer(
        {
            width: 1024,
            title: addLocationText,
            children:
                <AddLocationForm onSuccess={() => {
                    setTrigger(function(prev) { return !prev; });
                }} />,
            customClose: () => setTrigger(function(prev) { return !prev; })
        }
    );

    const uploadLocationDrawer = Drawer(
        {
            width: 800,
            title: uploadLocationText,
            children:
                <UploadLocationExcelForm onSuccess={() => {
                    setTrigger(function(prev) { return !prev; });
                }} />,
            customClose: () => setTrigger(function(prev) { return !prev; })
        }
    );

    const handleDelete = (record: any) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .post('/resource/locations/remove-location', {
                    name: record.name,
                    location: record.location,
                    source: record.source
                }, {})
                .then(() => {
                    openNotification('customSuccess', 'Successfully deleted.')
                    setTrigger(function(prev) { return !prev; });
                })
                .catch(function (error) {
                    openNotification('error', error);
                    setTrigger(function(prev) { return !prev; });
                });
        }
    };

    const handleCopy = (record: any) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .post(`/resource/locations/copy-location/${record.id}`)
                .then(() => {
                    openNotification('customSuccess', 'Successfully copied.')
                    setTrigger(function(prev) { return !prev; });
                })
                .catch(function (error) {
                    openNotification('error', error);
                });
        }
    };

    const action = (record: any) => {

        return (
            <>
                <Tooltip title={<Trans>Edit</Trans>}>
                    <EditOutlined className={"ActionButtons"} style={{ color: "#40a9ff" }}
                        onClick={() =>
                            addLocationDrawer
                                .open(
                                    "Edit",
                                    {
                                        id: record.id,
                                        name: record.name,
                                        location: record.location,
                                        source: record.source
                                    })
                        }
                    />
                </Tooltip>
                <Tooltip title={<Trans>Copy</Trans>}>
                    <CopyOutlined className={"ActionButtons"} style={{ color: "#40a9ff" }}
                        onClick={() => handleCopy(record)}
                    />
                </Tooltip>
                <Tooltip key="delete" title={<Trans>Remove</Trans>}>
                    <Popconfirm
                        title={<Trans>Are you sure want to delete record?</Trans>}
                        onConfirm={() => handleDelete(record)}
                        okText={<Trans>Yes</Trans>}
                        cancelText={<Trans>No</Trans>}>
                        <DeleteTwoTone className={"ActionButtons"} twoToneColor={"red"} />
                    </Popconfirm>

                </Tooltip>

            </>
        )
    };

    const actions = {
        width: 100,
        render: (text: string, record: any) => action(record),
        title: '',
        fixed: 'right',
        align: 'center'
    };

    const handleDownloadXls = () => {
        if (axiosInstance.current != null) {
            const FileDownload = require('js-file-download');
            axiosInstance.current({
                url: process.env.REACT_APP_API_BASE_URL + '/resource/locations/download-template?withSource=' + 'true',
                method: 'GET',
                responseType: 'blob'
            }).then((response) => {
                FileDownload(response.data, 'locationsUploadTemplate.xlsx');
            }).catch(function (error: any) {
                console.log(error);
            });
        }
    }

    const locProps: UploadProps = {
        name: 'file',
        multiple: false,
        showUploadList: false, // opcionális, ha nem kell lista
        customRequest: async (options) => {
            const { file, onSuccess, onError } = options;
            if (!axiosInstance.current) return;

            setLocLoading(true);
            const formData = new FormData();
            formData.append('file', file as any);
            formData.append('confirmed', 'true');

            try {
                await axiosInstance.current.post('/resource/locations/upload-xls-with-source', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                openNotification('customSuccess', 'File successfully uploaded');
                onSuccess && onSuccess({}, file as any);
            } catch (error) {
                openNotification('error', error);
                onError && onError(error as any);
            } finally {
                setLocLoading(false);
            }
        },
        onChange(info) {
            if (info.file.status === 'done') {
                // frissítés a sikeres feltöltés után
                setTrigger(function(prev) { return !prev; });
            }
        }
    };

    const header = (
        <>
            <Button type={'primary'} onClick={() => addLocationDrawer.open('Add new')}>
                <ButtonWithIcon icon={<PlusOutlined />} text={<Trans>Add new</Trans>} />
            </Button>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
            <Button onClick={() => handleDownloadXls()}>
                <ButtonWithIcon
                    icon={<DownloadOutlined />}
                    text={<Trans>.xlsx template</Trans>}
                />
            </Button>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
            <Upload {...locProps}>
                <Button loading={locLoading}>
                    <ButtonWithIcon icon={<UploadOutlined />} text={<Trans>.xlsx upload</Trans>} />
                </Button>
            </Upload>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
        </>
    );


    return (<>

        <>

            <Breadcrumb style={{ marginBottom: 10 }}>
                <Breadcrumb.Item><Trans id={'Home'} /></Breadcrumb.Item>
                <Breadcrumb.Item><Trans>Data Maintenance</Trans></Breadcrumb.Item>
                <Breadcrumb.Item><Trans>Locations of Relevant Commodity</Trans></Breadcrumb.Item>
            </Breadcrumb>
            <DtoTable
                tableHeader={header}
                model={LocationModel}
                action={actions}
                trigger={trigger}
                allowRefreshButton={true}
                allowExport={true}
                pageSize={100}
                scroll={{ x: 1300, y: "65vh" }} />
        </>
        {locationDrawer.component}
        {addLocationDrawer.component}
        {uploadLocationDrawer.component}
    </>
    )
}
export default LocationsScreen;
